import React, { useState, useEffect, useRef } from 'react';
import {
	CheckCircle,
	XCircle,
	RotateCw,
	AlertCircle,
	Search,
} from 'lucide-react';
import DOMPurify from 'dompurify';
import { Pagination } from '@mui/material';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import useDebounce from '@Hooks/useDebounce';
import ErrorHandler from '@Root/ErrorHandler';
import RegistryUploadJobService from '@Helpers/URLHelpers/WeboutService/RegistryUploadJobService';
import './RegistriesImportRow.scss';

export default function RegistriesImportRow(props) {

	const { item, cachedDetails, setCache } = props;
	const className = 'registriesImportRow';
	const [page, setPage] = useState(1);
	const [size] = useState(10);
	const [totalPages, setTotalPages] = useState(1);
	const [totalResult, setTotalResult] = useState(0);
	const [searchQuery, setSearchQuery] = useState('');
	const debouncedSearch = useDebounce(searchQuery, 500, true);
	const [registriesUploadList, setRegistriesUploadList] = useState(
		cachedDetails || [],
	);
	const [loader, setLoader] = useState(!cachedDetails);
	const [pageCache, setPageCache] = useState({});
	const registriesUploadListRef = useRef(null);
	const registryUploadJobService = new RegistryUploadJobService();
	const errorHandler = new ErrorHandler();
	const [filter, setFilter] = useState({
		registryUploadJobFilter: {
			runIdList: [item.id],
			conclusionList: [],
			statusList: [],
			activeList: [true],
			sortBy: 'startDateTimeStamp',
		},
	});

	useEffect(() => {
		setPage(1);
		setSearchQuery('');
		setPageCache({});
		setFilter({
			registryUploadJobFilter: {
				runIdList: [item.id],
				conclusionList: [],
				statusList: [],
				activeList: [true],
				sortBy: 'startDateTimeStamp',
			},
		});
		if (cachedDetails) {
			setRegistriesUploadList(cachedDetails);
			setLoader(false);
		} else {
			setRegistriesUploadList([]);
		}
	}, [item.id]);

	useEffect(() => {
		getTaskUploadJobData(page - 1);
	}, [
		item.id,
		page,
		size,
		debouncedSearch,
		filter.registryUploadJobFilter.conclusionList,
	]);

	const getTaskUploadJobData = async (apiPage) => {
		try {
			setLoader(true);
			const normalizedSearch = debouncedSearch?.trim().toLowerCase() || '';
			const conclusionList =
				filter?.registryUploadJobFilter?.conclusionList || [];
			const cacheKey = [
				item.id,
				apiPage,
				size,
				normalizedSearch,
				conclusionList.join(','),
			].join('|');
			if (pageCache[cacheKey]) {
				setRegistriesUploadList(pageCache[cacheKey]);
				setLoader(false);
				return;
			}
			const filterObj = {
				registryUploadJobFilter: {
					...filter.registryUploadJobFilter,
					runIdList: [item.id],
					searchQuery: normalizedSearch,
				},
				page: apiPage,
				size,
			};
			let response =
				await registryUploadJobService.getFilteredRegistryUploadJob(filterObj);
			if (response.status >= 400) {
				return;
			}
			response = JSON.parse(response.body);
			const registryUploadJobList =
				response?.registryUploadJobList?.map((job) => {
					let details = {};
					try {
						if (typeof job.details === 'string') {
							details = job.details ? JSON.parse(job.details) : {};
						} else {
							details = job.details || {};
						}
					} catch (error) {
						details = {};
					}
					return {
						...job,
						details,
					};
				}) || [];
			setTotalResult(response?.totalItems || 0);
			setTotalPages(response?.totalPages || 1);
			setRegistriesUploadList(registryUploadJobList);
			setPageCache((prev) => ({
				...prev,
				[cacheKey]: registryUploadJobList,
			}));
			setCache?.(registryUploadJobList);
		} catch (err) {
			errorHandler.handleError(err);
		} finally {
			setLoader(false);
		}
	};

	const handleConclusionChange = (value) => {
		setFilter((prev) => {
			const current = prev?.registryUploadJobFilter?.conclusionList || [];
			const conclusionList = current.includes(value)
				? current.filter((item) => item !== value)
				: [...current, value];
			return {
				...prev,
				registryUploadJobFilter: {
					...prev.registryUploadJobFilter,
					conclusionList,
				},
			};
		});
		setPage(1);
		setPageCache({});
	};

	const clearFilters = () => {
		setSearchQuery('');
		setFilter((prev) => ({
			...prev,
			registryUploadJobFilter: {
				...prev.registryUploadJobFilter,
				conclusionList: [],
			},
		}));
		setPage(1);
		setPageCache({});
	};

	const renderStatus = (result) => {
		if (result.conclusion === 'pass') {
			return (
				<span className={`${className}__status ${className}__status--success`}>
					<CheckCircle size={14} />
					Success
				</span>
			);
		}
		if (result.conclusion === 'running') {
			return (
				<span className={`${className}__status ${className}__status--running`}>
					<RotateCw size={14} className={`${className}__spin`} />
					Running
				</span>
			);
		}
		return (
			<span className={`${className}__status ${className}__status--failed`}>
				<XCircle size={14} />
				Failed
			</span>
		);
	};

	const getInitial = (name) => {
		if (!name) {
			return '?';
		}
		return name.trim().charAt(0).toUpperCase();
	};

	const getName = (row) => {
		const details = row?.details || {};
		const failed = row?.conclusion === 'fail';
		return (
			<div className={`${className}__contact`}>
				<div className={`${className}__avatar`}>{getInitial(details.name)}</div>
				<div>
					<strong>{details.name || '—'}</strong>
					{failed && (
						<span className={`${className}__contactWarning`}>
							Validation issue
						</span>
					)}
				</div>
			</div>
		);
	};

	const getEmail = (row) => {
		const details = row?.details || {};
		return (
			<span className={`${className}__value`}>{details.email || '—'}</span>
		);
	};

	const getContact = (row) => {
		const details = row?.details || {};
		const areaCode = details.areaCode ? `+${details.areaCode}` : '';
		const contactNumber = details.contactNumber || '';
		const phone =
			areaCode || contactNumber ? `${areaCode} ${contactNumber}`.trim() : '—';
		return <span className={`${className}__value`}>{phone}</span>;
	};

	const getErrorDetails = (row) => {
		const details = row?.details || {};
		const failed = row?.conclusion === 'fail';
		if (!failed) {
			return null;
		}
		if (details.message) {
			return (
				<div className={`${className}__error`}>
					<AlertCircle size={14} />
					<span>{details.message}</span>
				</div>
			);
		}
		return <span className={`${className}__noError`}>—</span>;
	};

	const emptyList = () => (
		<div className={`${className}__empty`}>
			<h4>No items for this filter.</h4>
		</div>
	);

	const isConclusionActive = (value) => filter?.registryUploadJobFilter?.conclusionList?.includes(value);

	const handleSearchChange = (event) => {
		const value = DOMPurify.sanitize(event.target.value);
		setSearchQuery(value);
		setPage(1);
		setPageCache({});
	};

	const getFooter = () => {
		if (totalPages <= 1) {
			return null;
		}
		return (
			<div className={`${className}__pagination`}>
				<Pagination
					count={totalPages}
					page={page}
					onChange={(_, newPage) => setPage(newPage)}
					shape='rounded'
					size='small'
				/>
				<span className={`${className}__resultCount`}>
					{totalResult} results
				</span>
			</div>
		);
	};

	return (
		<div className={className}>
			<div className={`${className}__toolbar`}>
				<div className={`${className}__search`}>
					<Search size={14} />
					<input
						type='text'
						value={searchQuery}
						onChange={handleSearchChange}
						placeholder='Search name, email, phone...'
					/>
				</div>
				<div className={`${className}__filters`}>
					<button
						type='button'
						className={`${className}__filterButton ${
							isConclusionActive('pass')
								? `${className}__filterButton--successActive`
								: ''
						}`}
						onClick={() => handleConclusionChange('pass')}
					>
						<CheckCircle size={13} />
						Success
					</button>
					<button
						type='button'
						className={`${className}__filterButton ${
							isConclusionActive('fail')
								? `${className}__filterButton--failedActive`
								: ''
						}`}
						onClick={() => handleConclusionChange('fail')}
					>
						<XCircle size={13} />
						Failed
					</button>
					{(searchQuery ||
						filter?.registryUploadJobFilter?.conclusionList?.length > 0) && (
						<button
							type='button'
							className={`${className}__clearFilter`}
							onClick={clearFilters}
						>
							Clear
						</button>
					)}
				</div>
			</div>
			<div className={`${className}__table-wrapper`}>
				{loader ? (
					<div className={`${className}__loader`}>
						<div className={`${className}__loaderSpinner`} />
						<span>Loading import details…</span>
					</div>
				) : (
					<DataTable
						value={registriesUploadList}
						ref={registriesUploadListRef}
						scrollable
						scrollHeight='flex'
						style={{
							height: '100%',
						}}
						columnResizeMode='expand'
						emptyMessage={emptyList}
						footer={getFooter}
						resizableColumns
						dataKey='id'
					>
						<Column field='contact' header='CONTACT' body={getName} />
						<Column field='email' header='EMAIL' body={getEmail} />
						<Column field='phone' header='PHONE' body={getContact} />
						<Column field='status' header='STATUS' body={renderStatus} />
						<Column
							field='error'
							header='ERROR DETAILS'
							body={getErrorDetails}
						/>
					</DataTable>
				)}
			</div>
		</div>
	);
}
